import { ref, computed } from 'vue';
import type { CollectionItemInterface } from '@/store/useCollectionsStore';
import { useCollectionsStore } from '@/store/useCollectionsStore';

export const useTemplateCollectionSelection = (initialCollectionIds?: string[]) => {
  const collectionsStore = useCollectionsStore();

  // Selected collection IDs (source of truth)
  const selectedCollectionIds = ref<string[]>(initialCollectionIds || []);

  // Collections to display (recently used - never removed, only added)
  const displayedCollections = ref<CollectionItemInterface[]>([]);

  // Initialize displayed collections from initial IDs
  if (initialCollectionIds && initialCollectionIds.length > 0) {
    const initial = collectionsStore.list.filter(c => initialCollectionIds.includes(c.id));
    displayedCollections.value = initial;
  }

  // If we have less than 5, add more from store
  if (displayedCollections.value.length < 5) {
    const displayedIds = new Set(displayedCollections.value.map(c => c.id));
    const additional = collectionsStore.list.filter(c => !displayedIds.has(c.id)).slice(0, 5 - displayedCollections.value.length);
    displayedCollections.value = [...displayedCollections.value, ...additional];
  }

  // Label for the checkbox group
  const collectionsLabel = computed(() => {
    const allSelected = displayedCollections.value.every(c => selectedCollectionIds.value.includes(c.id));
    return allSelected && selectedCollectionIds.value.length > 0 ? 'Selected collections' : 'Recently used';
  });

  // Toggle collection (add/remove from selected, add to displayed if not there)
  const toggleCollection = (collectionId: string) => {
    // Toggle selection - replace array to trigger reactivity
    const idsSet = new Set(selectedCollectionIds.value);
    if (idsSet.has(collectionId)) {
      idsSet.delete(collectionId);
      selectedCollectionIds.value = Array.from(idsSet);
    } else {
      selectedCollectionIds.value = [...selectedCollectionIds.value, collectionId];
    }

    // Add to displayed if not there
    const inDisplayed = displayedCollections.value.some(c => c.id === collectionId);
    if (!inDisplayed) {
      const collection = collectionsStore.list.find(c => c.id === collectionId);
      if (collection) {
        displayedCollections.value.push(collection);
      }
    }
  };

  // Get collection IDs for saving
  const getCollectionIdsForSaving = (): string[] => {
    return selectedCollectionIds.value;
  };

  return {
    // State
    selectedCollectionIds,
    displayedCollections,
    collectionsLabel,

    // Methods
    toggleCollection,
    getCollectionIdsForSaving,
  };
};
